import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { apiFetch } from "../lib/api";
import SEO from "../components/SEO.jsx";
import { seoPresets } from "../seoPresets";

const BASIC_PRICE_ID = import.meta.env.VITE_STRIPE_PRICE_BASIC_SUB;
const OUTREACH_PRICE_ID = import.meta.env.VITE_STRIPE_PRICE_OUTREACH_SUB;

const META_SUBSCRIBE_TRACK_KEY = "meta_subscribe_tracked";

const PIXEL_PRICE_MAP = {
  [BASIC_PRICE_ID]: { value: 0, currency: "USD", plan_name: "Basic" },
  [OUTREACH_PRICE_ID]: { value: 0, currency: "USD", plan_name: "Outreach" },
};

// A simple country list for the <select>. Add more if you like.
const COUNTRY_OPTIONS = [
  { code: "CZ", name: "Czech Republic" },
  { code: "SK", name: "Slovakia" },
  { code: "DE", name: "Germany" },
  { code: "AT", name: "Austria" },
  { code: "PL", name: "Poland" },
  { code: "US", name: "United States" },
  { code: "GB", name: "United Kingdom" },
  { code: "CA", name: "Canada" },
  { code: "AU", name: "Australia" },
  { code: "FR", name: "France" },
  { code: "ES", name: "Spain" },
  { code: "IT", name: "Italy" },
  { code: "NL", name: "Netherlands" },
  { code: "SE", name: "Sweden" },
  { code: "NO", name: "Norway" },
  { code: "FI", name: "Finland" },
  { code: "DK", name: "Denmark" },
  { code: "IE", name: "Ireland" },
  { code: "BE", name: "Belgium" },
  { code: "CH", name: "Switzerland" },
  { code: "PT", name: "Portugal" },
  { code: "HU", name: "Hungary" },
  { code: "RO", name: "Romania" },
  { code: "BG", name: "Bulgaria" },
  { code: "GR", name: "Greece" },
  { code: "HR", name: "Croatia" },
  { code: "SI", name: "Slovenia" },
  { code: "EE", name: "Estonia" },
  { code: "LV", name: "Latvia" },
  { code: "LT", name: "Lithuania" },
  { code: "UA", name: "Ukraine" },
  { code: "BR", name: "Brazil" },
  { code: "MX", name: "Mexico" },
  { code: "AR", name: "Argentina" },
  { code: "CL", name: "Chile" },
  { code: "JP", name: "Japan" },
  { code: "CN", name: "China" },
  { code: "IN", name: "India" },
  { code: "KR", name: "South Korea" },
  { code: "SG", name: "Singapore" },
  { code: "NZ", name: "New Zealand" },
];

export default function Profile() {
  const seo = seoPresets["/profile"];
  const loc = useLocation();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [displayName, setDisplayName] = useState("");
  const [billingName, setBillingName] = useState("");
  const [billingEmail, setBillingEmail] = useState("");
  const [country, setCountry] = useState("CZ");
  const [saving, setSaving] = useState(false);

  const [providerMap, setProviderMap] = useState({});
  const [linkedProviders, setLinkedProviders] = useState([]);

  const [activeTab, setActiveTab] = useState("subscription"); // "subscription" | "billing" | "linked" | "mailboxes"
  const [mailboxes, setMailboxes] = useState([]);
  const [mailboxLoading, setMailboxLoading] = useState(false);
  const [mailboxErr, setMailboxErr] = useState("");

  const locale =
    typeof navigator !== "undefined" ? navigator.language : "en-US";
  const fmtDate = (d) =>
    d ? new Date(d).toLocaleString(locale, { dateStyle: "medium" }) : "";

  const user = me?.user;
  const profile = me?.profile;
  const subscription = me?.subscription;

  const plan = useMemo(() => {
    const s = subscription;
    if (!s || s.status === "none") return "none";
    if (s.status === "free") return "free";

    const price = s.price_id;
    if (price === OUTREACH_PRICE_ID) return "outreach";

    const nm = String(s.name || "").toLowerCase();
    if (nm.includes("outreach")) return "outreach";

    return "unknown";
  }, [subscription]);

  const canSeeMailboxes = true; // All users can see mailboxes now

  const planLabel = useMemo(() => {
    if (plan === "outreach") return "Backstage Pass";
    if (plan === "free") return "Free";
    if (!subscription || subscription.status === "none") return "No plan";
    return subscription.name || "Plan";
  }, [plan, subscription]);

  const planStatusLabel = useMemo(() => {
    if (!subscription || subscription.status === "none") {
      return "No paid plan yet";
    }
    if (subscription.status === "free") {
      const remaining = subscription.reveals_remaining || 0;
      return `${remaining} reveals remaining this month`;
    }
    const s = subscription.status;
    const date = subscription.current_period_end
      ? fmtDate(subscription.current_period_end)
      : null;

    if (s === "trialing")
      return date ? `On free trial · ends ${date}` : "On free trial";
    if (s === "active" && subscription.cancel_at_period_end && date) {
      return `Cancels on ${date}`;
    }
    if (s === "active" && date) return `Renews on ${date}`;
    if (s === "canceled") return "Canceled";
    return s.charAt(0).toUpperCase() + s.slice(1);
  }, [subscription, fmtDate]);
  useEffect(() => {
    if (!subscription) return;

    const priceId = subscription.price_id;
    const status = subscription.status;

    // Only care about users who actually have a subscription/trial
    if (!priceId) return;
    if (!["trialing", "active", "past_due"].includes(status)) return;

    if (typeof window === "undefined" || typeof window.fbq !== "function") {
      return;
    }

    // Optional: only fire when they came back from Stripe with ?success=1
    const params = new URLSearchParams(loc.search || "");
    const fromCheckout = params.get("success") === "1";
    if (!fromCheckout) return;

    // De-dupe using localStorage so we don't fire repeatedly
    let stored = null;
    try {
      const raw = window.localStorage.getItem(META_SUBSCRIBE_TRACK_KEY);
      stored = raw ? JSON.parse(raw) : null;
    } catch {
      stored = null;
    }

    if (
      stored &&
      stored.price_id === priceId &&
      stored.status === status
    ) {
      // Already tracked this subscription state in this browser
      return;
    }

    const meta =
      PIXEL_PRICE_MAP[priceId] || {
        value: 0,
        currency: "USD",
        plan_name: planLabel,
      };

    try {
      console.log("Will fire Subscribe pixel", { priceId, status, fromCheckout });
      window.fbq("track", "Subscribe", {
        value: meta.value,
        currency: meta.currency,
        plan_name: meta.plan_name,
        price_id: priceId,
        status,
      });

      window.localStorage.setItem(
        META_SUBSCRIBE_TRACK_KEY,
        JSON.stringify({
          price_id: priceId,
          status,
          fired_at: new Date().toISOString(),
        })
      );
    } catch (e) {
      // never let tracking break the app
      console.warn("Meta Pixel Subscribe error", e);
    }
  }, [subscription, planLabel, loc.search]);

  const googleLinked = linkedProviders.includes("google");

  const initials = useMemo(() => {
    const name = profile?.display_name || user?.name || user?.email || "";
    const parts = String(name).trim().split(/\s+/);
    const first = parts[0]?.[0] ?? "";
    const second = parts[1]?.[0] ?? "";
    return (first + second).toUpperCase() || "U";
  }, [profile, user]);

  const refresh = async () => {
    setLoading(true);
    setErr("");
    try {
      const j = await apiFetch("/api/me");
      setMe(j);

      setDisplayName(j?.profile?.display_name || j?.user?.name || "");
      setBillingName(j?.profile?.billing_name || j?.user?.name || "");
      setBillingEmail(j?.profile?.billing_email || j?.user?.email || "");

      // Country priority:
      // 1) existing billing_address.country
      // 2) request_country from backend (derived from IP headers)
      // 3) fallback "CZ"
      const initialCountry =
        j?.profile?.billing_address?.country ||
        j?.request_country ||
        "CZ";
      setCountry(initialCountry);

      const prov = await apiFetch("/api/auth/providers").catch(() => ({}));
      setProviderMap(prov?.providers || {});
      setLinkedProviders(Array.isArray(prov?.linked) ? prov.linked : []);
    } catch (e) {
      setErr("Not signed in or server unavailable.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let ok = true;
    (async () => {
      await refresh();
      if (!ok) return;
    })();
    return () => {
      ok = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function saveProfile(e) {
    e.preventDefault();
    setSaving(true);
    setErr("");
    try {
      await apiFetch("/api/profile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          display_name: displayName || undefined,
          billing_name: billingName || undefined,
          billing_email: billingEmail || undefined,
          billing_address: { country: country || "CZ" },
        }),
      });
      await refresh();
    } catch (e) {
      setErr(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function onGoogleLinkSuccess(cred) {
    try {
      await apiFetch("/api/auth/link/google", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ credential: cred.credential }),
      });
      await refresh();
    } catch (e) {
      setErr(e.message || "Link failed");
    }
  }

  async function unlinkGoogle() {
    if (
      !confirm(
        "Unlink Google from your account? You can link it again later."
      )
    )
      return;
    try {
      await apiFetch("/api/auth/unlink/google", { method: "POST" });
      await refresh();
    } catch (e) {
      setErr(e.message || "Unlink failed");
    }
  }

  const openPortal = async () => {
    try {
      const j = await apiFetch("/api/stripe/portal", { method: "POST" });
      if (j?.url) window.location.href = j.url;
      else alert("Could not open billing portal.");
    } catch {
      alert("Could not open billing portal.");
    }
  };

  // ------- Mailboxes helpers (Outreach tab) -------

  async function loadMailboxes() {
    setMailboxLoading(true);
    setMailboxErr("");
    try {
      const j = await apiFetch("/api/mailboxes");
      const items = Array.isArray(j?.items) ? j.items : [];
      setMailboxes(items);
    } catch (e) {
      setMailboxErr(e.message || "Failed to load mailboxes");
      setMailboxes([]);
    } finally {
      setMailboxLoading(false);
    }
  }

  useEffect(() => {
    if (activeTab === "mailboxes" && canSeeMailboxes) {
      loadMailboxes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, canSeeMailboxes]);

  async function startMailbox(provider) {
    try {
      const path =
        provider === "google"
          ? "/api/mailboxes/google/start"
          : "/api/mailboxes/outlook/start";
      const j = await apiFetch(path, { method: "POST" });
      if (j?.url) {
        window.location.href = j.url;
      } else {
        setMailboxErr("Could not open provider authorization.");
      }
    } catch (e) {
      setMailboxErr(e.message || "Could not start mailbox connection");
    }
  }

  async function unlinkMailbox(provider) {
    if (!confirm("Disconnect this mailbox? You can reconnect later.")) return;
    try {
      await apiFetch(`/api/mailboxes/${provider}/unlink`, {
        method: "POST",
      });
      await loadMailboxes();
    } catch (e) {
      setMailboxErr(e.message || "Failed to disconnect mailbox");
    }
  }

  const hasConnectedMailbox = mailboxes.some(
    (m) => (m.status || "").toLowerCase() === "connected"
  );

  // ------------------------------------------------

  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        image={seo.openGraph?.image}
        jsonLd={seo.jsonLd}
        ogType={seo.openGraph?.type || "website"}
        twitterCard={seo.twitter?.card || "summary_large_image"}
        robots="noindex, nofollow"
      />
      <div className="page-scroll">
        <div className="prof-wrap">
          <style>{css}</style>

          <header className="prof-head">
            <div className="prof-id">
              <div className="avatar" aria-hidden>
                {initials}
              </div>
              <div className="meta">
                <h1>{profile?.display_name || user?.name || "Member"}</h1>
                <div className="sub">{user?.email}</div>
              </div>
            </div>

            <div className="plan-pill" title="Current subscription">
              <span className="dot" />
              <span className="plan-pill-main">{planLabel}</span>
              <span className="plan-pill-sub">{planStatusLabel}</span>
            </div>
          </header>

          {loading ? (
            <div className="skeleton">Loading profile…</div>
          ) : err ? (
            <div className="alert error">{err}</div>
          ) : (
            <>
              {/* Tabs */}
              <div className="settings-tabs">
                <button
                  className={
                    "settings-tab" +
                    (activeTab === "subscription"
                      ? " settings-tab--active"
                      : "")
                  }
                  onClick={() => setActiveTab("subscription")}
                  type="button"
                >
                  Your Subscription
                </button>

                <button
                  className={
                    "settings-tab" +
                    (activeTab === "billing" ? " settings-tab--active" : "")
                  }
                  onClick={() => setActiveTab("billing")}
                  type="button"
                >
                  Account &amp; Billing
                </button>

                <button
                  className={
                    "settings-tab" +
                    (activeTab === "linked" ? " settings-tab--active" : "")
                  }
                  onClick={() => setActiveTab("linked")}
                  type="button"
                >
                  Linked Accounts
                </button>

                {canSeeMailboxes && (
                  <button
                    className={
                      "settings-tab" +
                      (activeTab === "mailboxes"
                        ? " settings-tab--active"
                        : "")
                    }
                    onClick={() => setActiveTab("mailboxes")}
                    type="button"
                  >
                    Mailboxes
                  </button>
                )}
              </div>

              <div className="tab-stack">
                {/* Subscription tab */}
                {activeTab === "subscription" && (
                  <section className="card sub-card">
                    <div className="card-head">
                      <h3>Your subscription</h3>
                      {subscription && subscription.status !== "free" && subscription.status !== "none" ? (
                        <button className="btn" onClick={openPortal}>
                          Manage billing
                        </button>
                      ) : (
                        <Link to="/pricing" className="btn primary">
                          Upgrade plan
                        </Link>
                      )}
                    </div>

                    <div className="sub-top">
                      <div className="plan">
                        <span className="pill">{planLabel}</span>
                        <div className="plan-sub">{planStatusLabel}</div>
                      </div>
                    </div>

                    <PlanFeatureList plan={plan} />
                  </section>
                )}

                {/* Account & Billing tab */}
                {activeTab === "billing" && (
                  <section className="card">
                    <div className="card-head">
                      <h3>Account &amp; billing</h3>
                    </div>

                    <form className="rows" onSubmit={saveProfile}>
                      <LabelRow label="Display name">
                        <input
                          value={displayName}
                          onChange={(e) => setDisplayName(e.target.value)}
                        />
                      </LabelRow>

                      <LabelRow label="Email">
                        <div className="val mono">{user?.email || "—"}</div>
                      </LabelRow>

                      <div className="sep" />

                      <div className="row">
                        <div className="lbl">Billing details</div>
                        <div className="val muted small">
                          Shown on Stripe receipts.
                        </div>
                      </div>

                      <LabelRow label="Billing name">
                        <input
                          value={billingName}
                          onChange={(e) => setBillingName(e.target.value)}
                        />
                      </LabelRow>

                      <LabelRow label="Billing email">
                        <input
                          type="email"
                          value={billingEmail}
                          onChange={(e) => setBillingEmail(e.target.value)}
                        />
                      </LabelRow>

                      <LabelRow label="Country">
                        <select
                          value={country || ""}
                          onChange={(e) => setCountry(e.target.value)}
                        >
                          <option value="">Select country…</option>
                          {COUNTRY_OPTIONS.map((c) => (
                            <option key={c.code} value={c.code}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </LabelRow>

                      <div className="row row-actions">
                        <div className="lbl" />
                        <div className="val">
                          <button
                            className="btn primary block-sm"
                            disabled={saving}
                          >
                            {saving ? "Saving…" : "Save changes"}
                          </button>
                        </div>
                      </div>
                    </form>
                  </section>
                )}

                {/* Linked accounts tab */}
                {activeTab === "linked" && (
                  <section className="card">
                    <div className="card-head">
                      <h3>Linked accounts</h3>
                    </div>

                    <ul className="link-list">
                      <li>
                        Google:{" "}
                        <strong>{googleLinked ? "linked" : "not linked"}</strong>
                      </li>
                    </ul>

                    {!googleLinked ? (
                      <div className="auth-link">
                        <GoogleOAuthProvider
                          clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}
                        >
                          <GoogleLogin
                            text="continue_with"
                            onSuccess={onGoogleLinkSuccess}
                            onError={() => setErr("Google link failed")}
                          />
                        </GoogleOAuthProvider>
                      </div>
                    ) : (
                      <div className="auth-link">
                        <button className="btn" onClick={unlinkGoogle}>
                          Unlink Google
                        </button>
                      </div>
                    )}
                  </section>
                )}

                {/* Mailboxes tab */}
                {activeTab === "mailboxes" && canSeeMailboxes && (
                  <section className="card mailboxes-card">
                    <div className="card-head">
                      <h3>Mailboxes</h3>
                      <button
                        className="btn"
                        type="button"
                        onClick={loadMailboxes}
                        disabled={mailboxLoading}
                      >
                        {mailboxLoading ? "Refreshing…" : "Refresh"}
                      </button>
                    </div>

                    <p className="muted small">
                      Connect your email to send Outreach campaigns from your own
                      inbox. You can also connect from the Campaigns page, but
                      this is your central place to manage mailboxes.
                    </p>

                    {mailboxErr && (
                      <div className="alert error" style={{ marginTop: 8 }}>
                        {mailboxErr}
                      </div>
                    )}

                    <div className="mailbox-connect-grid">
                      <button
                        type="button"
                        className="btn mailbox-connect-btn"
                        onClick={() => startMailbox("google")}
                      >
                        <span className="mailbox-pill mailbox-pill--google">
                          🟢
                        </span>
                        <span>Connect Google (Gmail / Workspace)</span>
                      </button>
                      <button
                        type="button"
                        className="btn mailbox-connect-btn"
                        disabled
                        style={{ opacity: 0.5, cursor: "not-allowed" }}
                      >
                        <span className="mailbox-pill mailbox-pill--outlook">🔵</span>
                        <span>Outlook (Coming soon)</span>
                      </button>
                      <button
                        type="button"
                        className="btn mailbox-connect-btn"
                        disabled
                        style={{ opacity: 0.6 }}
                      >
                        <span className="mailbox-pill">✉️</span>
                        <span>Other (IMAP / SMTP) — soon</span>
                      </button>
                    </div>

                    <div className="sep" />

                    <div className="mailbox-list-head">
                      <h4>Linked mailboxes</h4>
                      {hasConnectedMailbox && (
                        <span className="muted small">
                          Sending uses your primary connected mailbox.
                        </span>
                      )}
                    </div>

                    {mailboxLoading ? (
                      <div className="muted small">Loading mailboxes…</div>
                    ) : !mailboxes.length ? (
                      <div className="muted small">
                        No mailboxes connected yet. Connect Google or Outlook
                        above.
                      </div>
                    ) : (
                      <ul className="mailbox-list">
                        {mailboxes.map((m) => (
                          <li key={m.id} className="mailbox-row">
                            <div>
                              <div className="mailbox-email">
                                {m.email || "Unknown address"}
                              </div>
                              <div className="mailbox-sub">
                                {m.provider} • {m.status}
                              </div>
                            </div>
                            <div className="mailbox-actions">
                              <button
                                type="button"
                                className="btn"
                                onClick={() => unlinkMailbox(m.provider)}
                              >
                                Disconnect
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

function LabelRow({ label, children }) {
  return (
    <div className="row">
      <div className="lbl">{label}</div>
      <div className="val">{children}</div>
    </div>
  );
}

function PlanFeatureList({ plan }) {
  if (plan === "outreach") {
    return (
      <ul className="feature-list">
        <li>
          <b>Unlimited playlist searches</b>
        </li>
        <li>
          <b>Unlimited contact reveals</b>
        </li>
        <li>
          <b>Email campaigns</b> &amp; follow-ups
        </li>
        <li>
          <b>Reply tracking &amp; stats</b>
        </li>
        <li>
          <b>Priority support</b>
        </li>
        <li>Best for serious playlist outreach.</li>
      </ul>
    );
  }

  if (plan === "free") {
    return (
      <ul className="feature-list">
        <li>
          <b>Unlimited playlist searches</b>
        </li>
        <li>
          <b>10 contact reveals</b> per month
        </li>
        <li>
          <b>Email campaigns</b> &amp; follow-ups
        </li>
        <li>
          <b>Full playlist database</b> access
        </li>
        <li>
          <b>Exports</b> (CSV / TSV)
        </li>
        <li>Perfect for trying out MusicBase.</li>
      </ul>
    );
  }

  // No plan yet
  return (
    <div className="no-plan">
      <p className="muted">
        You don't have a paid plan yet. Upgrade to <b>Backstage Pass</b> for unlimited reveals and priority support.
      </p>
      <Link to="/pricing" className="btn primary">
        View plans
      </Link>
    </div>
  );
}

const css = `
:root{
  --bg:#f7f9fc; --card:#fff; --text:#0b0b0c; --muted:#6b7280; --b:#e6e9f0;
  --accent:#6E5BFF; --accent2:#A78BFA; --ring:rgba(110,91,255,.25);
}
.prof-wrap {
  padding: 22px;
  color: var(--text);
  background: #f7f9fc;
  min-height: 100vh;
}
@media (max-width:720px){
  .prof-wrap{ padding:14px; }
}

.prof-head{
  display:flex; align-items:center; justify-content:space-between; gap:12px; margin:8px 0 12px;
  flex-wrap:wrap;
}
.prof-id{ display:flex; align-items:center; gap:12px; min-width:0; }
.avatar{
  width:56px; height:56px; border-radius:16px;
  display:grid; place-items:center; font-weight:900;
  background:linear-gradient(135deg,var(--accent),var(--accent2));
  color:#fff; box-shadow:0 10px 28px rgba(110,91,255,.28); letter-spacing:.5px;
  flex:0 0 auto;
}
.meta h1{ margin:0; font-size:22px; line-height:1.1; }
.meta .sub{ color:var(--muted); font-size:13px; overflow:auto; text-overflow:ellipsis; white-space:nowrap; max-width:60vw; }
@media (max-width:720px){
  .avatar{ width:48px; height:48px; border-radius:12px; font-size:18px; }
  .meta h1{ font-size:18px; }
  .meta .sub{ max-width:70vw; }
}

/* Plan pill */
.plan-pill{
  display:inline-flex; align-items:center; gap:8px; flex:0 0 auto;
  background:linear-gradient(90deg,#f1efff,#eef5ff);
  border:1px solid var(--b); padding:10px 14px; border-radius:999px; font-weight:700;
  white-space:nowrap;
}
.plan-pill .dot{
  width:8px;height:8px;border-radius:999px;background:#7c3aed;
  box-shadow:0 0 0 4px rgba(124,58,237,.15);
}
.plan-pill-main{ font-weight:800; }
.plan-pill-sub{ color:var(--muted); font-size:12px; }
@media (max-width:720px){
  .plan-pill{ padding:8px 12px; font-size:13px; }
}

/* Tabs inside settings */
.settings-tabs{
  display:flex;
  gap:16px;
  margin:4px 0 16px;
  border-bottom:1px solid var(--b);
}
.settings-tab{
  border:0;
  background:none;
  padding:8px 2px 10px;
  font-weight:600;
  font-size:14px;
  color:var(--muted);
  cursor:pointer;
  position:relative;
}
.settings-tab--active{
  color:var(--text);
}
.settings-tab--active::after{
  content:"";
  position:absolute;
  left:0;
  right:0;
  bottom:-1px;
  height:2px;
  border-radius:999px;
  background:var(--accent);
}

/* Stack for tab content */
.tab-stack{
  display:flex;
  flex-direction:column;
  gap:16px;
  margin-top:8px;
}

/* Layout grid (kept if you reuse somewhere) */
.grid{ display:grid; grid-template-columns: 1.2fr 1fr 1fr; gap:16px; }
@media (max-width:1200px){ .grid{ grid-template-columns: 1fr 1fr; } }
@media (max-width:900px){ .grid{ grid-template-columns: 1fr; } }

.card{
  background:var(--card); border:1px solid var(--b); border-radius:18px; padding:16px;
  box-shadow:0 14px 40px rgba(16,24,40,.08);
}

/* Single scroll container on this page */
.page-scroll {
  height: calc(100svh - var(--nav-h, 64px));
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-gutter: stable;
}
.page-scroll {
  scrollbar-width: thin;
  scrollbar-color: #a78bfa #eef1ff;
}
.page-scroll::-webkit-scrollbar { width: 10px; }
.page-scroll::-webkit-scrollbar-track {
  background: #eef1ff;
  border-left: 1px solid var(--nav-border, rgba(17,24,39,.08));
}
.page-scroll::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #c7d2fe, #a78bfa);
  border: 2px solid #eef1ff;
  border-radius: 8px;
}

/* Subscription card */
.sub-card{
  background:
    radial-gradient(900px 600px at 110% -10%, rgba(110,91,255,.10) 0, transparent 60%),
    radial-gradient(700px 600px at -10% 110%, rgba(167,139,250,.12) 0, transparent 55%),
    var(--card);
}
.card-head{ display:flex; justify-content:space-between; align-items:center; margin-bottom:8px; gap:8px; }
.card-head h3{ margin:0; font-size:18px; }

.sub-top{ margin:8px 0 12px; }
.pill{
  display:inline-flex; align-items:center; justify-content:center; gap:8px;
  padding:8px 12px; border-radius:999px; font-weight:800;
  background:linear-gradient(90deg,#eef2ff,#f5f3ff); border:1px solid var(--b);
}
.plan-sub{ color:var(--muted); font-size:12px; margin-top:4px; }

.feature-list{
  display:grid; gap:8px; padding:0; margin:10px 0 0; list-style:none;
}
.feature-list li{ display:flex; align-items:center; gap:8px; }
.feature-list li::before{
  content:"✓"; display:inline-grid; place-items:center;
  width:18px; height:18px; border-radius:999px; border:1px solid var(--b);
}

.no-plan{ margin-top:8px; display:flex; flex-direction:column; gap:10px; }

/* Form layout */
.rows{ display:grid; gap:10px; margin:6px 0; }
.row{ display:grid; grid-template-columns: 180px 1fr; align-items:center; gap:10px; }
.lbl{ color:var(--muted); font-size:13px; }
.val{ font-weight:600; }
.val input,
.val select{
  width:100%; padding:12px 12px; border:1px solid var(--b); border-radius:12px; font-weight:600; background:#fff;
  min-height:44px;
}
.val input:focus,
.val select:focus{
  outline:none; box-shadow:0 0 0 6px var(--ring); border-color:#cfd7ff;
}
.val.mono{ font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; }
.hint{ display:block; color:#8a93a3; font-size:12px; margin-top:6px; }

@media (max-width:720px){
  .row{ grid-template-columns: 1fr; }
  .lbl{ font-size:12px; }
  .row-actions .val .btn{ width:100%; }
}

.sep{ height:1px; background:var(--b); margin:8px 0; }

.alert{ padding:12px; border-radius:12px; border:1px solid var(--b); background:#fff; }
.alert.error{ border-color:#ffd3d3; background:#fff4f4; color:#7a0c0c; }

.skeleton{ padding:12px; color:var(--muted); }

/* Buttons */
.btn{
  border:1px solid var(--b); padding:10px 12px; border-radius:12px;
  background:#fff; cursor:pointer; text-decoration:none; color:inherit; font-weight:700;
}
.btn:hover{ box-shadow:0 0 0 6px var(--ring); border-color:#cfd7ff; }
.btn.primary{
  color:#fff; background:linear-gradient(90deg,var(--accent),var(--accent2));
  border-color:transparent; box-shadow:0 14px 34px rgba(110,91,255,.18);
}
.btn.ghost{ background:#fff; }
.block-sm{ }

.muted{ color:var(--muted); }
.small{ font-size:12px; }

/* Linked accounts */
.link-list{ list-style:none; padding:0; margin:0; display:grid; gap:6px; }
.auth-link{ margin-top:8px; display:flex; gap:8px; flex-wrap:wrap; }

/* Mailboxes tab */
.mailboxes-card{ max-width:840px; }
.mailbox-connect-grid{
  display:flex;
  flex-wrap:wrap;
  gap:8px;
  margin:10px 0 12px;
}
.mailbox-connect-btn{
  display:inline-flex;
  align-items:center;
  gap:8px;
}
.mailbox-pill{
  width:24px;
  height:24px;
  border-radius:999px;
  display:inline-grid;
  place-items:center;
  background:#f3f4ff;
}
.mailbox-pill--google{ background:#e5f9ea; }
.mailbox-pill--outlook{ background:#e4f0ff; }

.mailbox-list-head{
  display:flex;
  justify-content:space-between;
  align-items:flex-end;
  gap:8px;
  margin-bottom:6px;
}
.mailbox-list{
  list-style:none;
  padding:0;
  margin:0;
  display:grid;
  gap:8px;
}
.mailbox-row{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:8px;
  padding:10px 12px;
  border-radius:12px;
  background:#f9fafb;
}
.mailbox-email{ font-weight:600; }
.mailbox-sub{ font-size:12px; color:var(--muted); }
.mailbox-actions{ display:flex; gap:8px; }

@media (max-width:720px){
  .mailbox-row{
    flex-direction:column;
    align-items:flex-start;
  }
}
`;
